/**
 * Centralized environment variable validation and access
 * Validates all required variables at app startup
 */

type RequiredEnvVars = {
  // Authentication
  readonly clientId: string;
  readonly authority: string;
  readonly redirectUri: string;
  readonly scopeId: string;
  // API
  readonly dbsBaseUrl: string;
};

function validateEnvVar(key: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(
      `❌ Missing required environment variable: ${key}\n` +
        `Please ensure it's defined in your .env file or build environment.`,
    );
  }
  return value;
}

// Validate and freeze environment variables at module load time
const requiredEnvVars: RequiredEnvVars = {
  clientId: validateEnvVar('VITE_APP_CLIENT_ID', import.meta.env.VITE_APP_CLIENT_ID),
  authority: validateEnvVar('VITE_APP_AUTHORITY', import.meta.env.VITE_APP_AUTHORITY),
  redirectUri: validateEnvVar('VITE_APP_REDIRECT_URI', import.meta.env.VITE_APP_REDIRECT_URI),
  scopeId: validateEnvVar('VITE_APP_SCOPE_ID', import.meta.env.VITE_APP_SCOPE_ID),
  dbsBaseUrl: validateEnvVar('VITE_DBS_BASE_URL', import.meta.env.VITE_DBS_BASE_URL),
} as const;

// Log loaded config
if (import.meta.env.DEV) {
  console.group('🔧 Environment Configuration');
  console.log('Auth:');
  console.log('  • Client ID:', requiredEnvVars.clientId);
  console.log('  • Scope ID:', requiredEnvVars.scopeId);
  console.log('  • Authority:', requiredEnvVars.authority);
  console.log('  • Redirect URI:', requiredEnvVars.redirectUri);
  console.log('Backend API:');
  console.log('  • Base URL:', requiredEnvVars.dbsBaseUrl);
  console.groupEnd();
}

export const env = {
  ...requiredEnvVars,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  mode: import.meta.env.MODE,
} as const;
